<script lang="ts">
  /**
   * CategoryLegend — horizontal list of cognitive categories with
   * click-to-toggle filtering. Owned state is the `hiddenCategories`
   * set; parent passes it in and a callback to mutate.
   */
  import { CATEGORIES, categoryMeta, type Category } from '../../design/categories';

  type Props = {
    hiddenCategories: ReadonlySet<Category>;
    onToggle: (category: Category) => void;
  };

  let { hiddenCategories, onToggle }: Props = $props();
</script>

<div class="legend" role="group" aria-label="Category filter">
  {#each CATEGORIES as cat (cat)}
    {@const meta = categoryMeta[cat]}
    {@const isHidden = hiddenCategories.has(cat)}
    <button
      type="button"
      class="legend-item"
      class:is-hidden={isHidden}
      onclick={() => onToggle(cat)}
      aria-pressed={!isHidden}
      title={meta.description}
    >
      <span class="swatch" style:background={meta.color} aria-hidden="true"></span>
      <span class="label">{meta.label}</span>
    </button>
  {/each}
</div>

<style>
  .legend {
    display: grid;
    grid-template-columns: repeat(5, max-content);
    gap: 4px 16px;
    justify-content: start;
    padding: var(--space-sm) 0;
  }

  @media (max-width: 640px) {
    .legend {
      grid-template-columns: repeat(3, max-content);
    }
  }

  .legend-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 6px;
    border: 1px solid transparent;
    border-radius: var(--radius-sm);
    background: transparent;
    cursor: pointer;
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    color: var(--ink);
    transition:
      opacity 150ms ease,
      border-color 150ms ease,
      background 150ms ease;
  }

  .legend-item:hover {
    background: var(--surface);
    border-color: var(--border);
  }

  .legend-item.is-hidden {
    opacity: 0.45;
  }

  .legend-item.is-hidden .swatch {
    opacity: 0.35;
  }

  .swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .label {
    white-space: nowrap;
  }
</style>
